import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Paperclip, Undo2, AlertTriangle } from "lucide-react";
import { LeaveCalendar } from "@/components/payroll/LeaveCalendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { Panel, Loading, Empty, Th, Td, TableWrap, NativeSelect } from "@/components/kit";
import { dmy, errMsg } from "@/lib/format";

const TONE = { pending: "bg-amber-50 text-amber-800 border-amber-200", approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200", cancelled: "bg-slate-50 text-slate-500 border-slate-200" };

export const LeaveStatus = ({ s, t }) => <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TONE[s]}`} data-testid={`leave-status-${s}`}>{t(`leave_st_${s}`)}</span>;

const Row = ({ lv, bal, rw, onDone }) => {
  const { api, t, download } = useApp();
  const [note, setNote] = useState("");
  const decide = async (decision) => {
    if (decision === "approved" && lv.overlaps?.length && !window.confirm(`${lv.overlaps.join(", ")} ${t("leave_overlap_confirm")}`)) return;
    try {
      const { data } = await api.post(`/payroll/leaves/${lv.id}/decide`, { decision, note });
      toast.success(t(`leave_st_${decision}`));
      if (data.synced_runs?.length) toast.info(`${t("leave_synced")} ${data.synced_runs.join(", ")}`);
      if (data.locked_runs?.length) toast.warning(`${t("leave_locked")} ${data.locked_runs.join(", ")}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async () => {
    if (!window.confirm(t("leave_revoke_confirm"))) return;
    try { const { data } = await api.post(`/payroll/leaves/${lv.id}/revoke`); toast.success(t("leave_st_cancelled")); if (data.synced_runs?.length) toast.info(`${t("leave_synced")} ${data.synced_runs.join(", ")}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-b border-slate-50" data-testid={`leave-row-${lv.id}`}>
      <Td><b>{lv.employee.first_name} {lv.employee.last_name}</b><div className="num text-[11px] text-slate-500">{lv.employee.code} · {t("leave_balance")} {bal?.available ?? "—"} j</div></Td>
      <Td>{t(`leave_t_${lv.type}`)}{lv.reason && <div className="text-[11px] text-slate-500">{t(`leave_r_${lv.reason}`)}</div>}</Td>
      <Td className="num">{dmy(lv.start)} → {dmy(lv.end)}</Td>
      <Td className="num text-end">{lv.days}</Td>
      <Td className="max-w-[220px] text-xs text-slate-600">{lv.note}{lv.manager_note && <div className="text-slate-400">↳ {lv.manager_note}</div>}
        {lv.attachment_file_id && <Button size="sm" variant="ghost" onClick={() => download(`/payroll/leaves/${lv.id}/attachment`, lv.attachment_name)} data-testid={`leave-attachment-${lv.id}`}><Paperclip className="h-3.5 w-3.5" /></Button>}</Td>
      <Td><LeaveStatus s={lv.status} t={t} />{lv.overlaps?.length > 0 && <div className="mt-1 flex items-center gap-1 text-[11px] text-[#B8000F]" data-testid={`leave-overlap-${lv.id}`}><AlertTriangle className="h-3 w-3" />{lv.overlaps.join(", ")} {t("leave_overlap_short")}</div>}</Td>
      <Td className="text-end whitespace-nowrap">{rw && lv.status === "approved" && <Button size="sm" variant="ghost" onClick={revoke} data-testid={`leave-revoke-${lv.id}`}><Undo2 className="h-4 w-4" /></Button>}
        {rw && lv.status === "pending" && <span className="inline-flex items-center gap-1">
        <Input className="h-8 w-36" placeholder={t("leave_note")} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`leave-note-${lv.id}`} />
        <Button size="sm" onClick={() => decide("approved")} data-testid={`leave-approve-${lv.id}`}><Check className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" onClick={() => decide("rejected")} data-testid={`leave-reject-${lv.id}`}><X className="h-4 w-4 text-red-600" /></Button>
      </span>}</Td>
    </tr>
  );
};

export const Leaves = () => {
  const { api, t, can } = useApp();
  const [status, setStatus] = useState("pending");
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => api.get("/payroll/leaves", { params: { status: status || undefined } }).then((r) => { setD(r.data); setTick((x) => x + 1); }), [api, status]);
  useEffect(() => { load(); }, [load]);
  const filter = <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leaves-filter">
    <option value="">{t("all")}</option>{["pending", "approved", "rejected", "cancelled"].map((s) => <option key={s} value={s}>{t(`leave_st_${s}`)}</option>)}</NativeSelect>;
  return (
    <div className="space-y-6">
    <LeaveCalendar reload={Math.max(tick - 1, 0)} />
    <Panel title={t("payroll_tab_leaves")} action={filter} testid="leaves-panel">
      {!d ? <Loading /> : d.requests.length === 0 ? <Empty /> : (
        <TableWrap>
          <thead className="border-b"><tr><Th>{t("leave_employee")}</Th><Th>{t("leave_type")}</Th><Th>{t("leave_period")}</Th><Th className="text-end">{t("leave_days")}</Th><Th>{t("leave_comment")}</Th><Th>{t("status")}</Th><Th /></tr></thead>
          <tbody>{d.requests.map((lv) => <Row key={lv.id} lv={lv} bal={d.balances[lv.employee_id]} rw={can("payroll", true)} onDone={load} />)}</tbody>
        </TableWrap>
      )}
      <p className="mt-3 text-xs text-slate-500">{t("leave_help")}</p>
    </Panel>
    </div>
  );
};
